import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Budget} from '../../interfaces';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortHeader, SortDirection} from '@angular/material/sort';
import {Subject, Subscription} from 'rxjs';
import {BudgetListFilter, BudgetListResponse, BudgetListTableService} from '../../services/budget-list-table.service';
import {AuthService} from '../../services/auth.service';
import {filter, takeUntil} from 'rxjs/operators';
import {Router, RouterLink} from '@angular/router';
import {firestoreDateToJsDate} from '../../shared/utils/utils';
import moment from 'moment';
import {OpportunityStages} from '../../opportunities/interfaces/opportunity-stages.enum';
import {OpportunityStatuses} from '../../opportunities/interfaces/opportunity-statuses.enum';
import {ProjectNrPipe} from '../../shared/pipes/project-nr.pipe';
import {CommonModule} from '@angular/common';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {TimestampPipe} from '../../shared/pipes/timestamp.pipe';

@Component({
    selector: 'app-budget-list-table',
    templateUrl: './budget-list-table.component.html',
    styleUrls: ['./budget-list-table.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatProgressBarModule,
        MatTableModule,
        MatSort,
        MatSortHeader,
        MatIconModule,
        MatTooltipModule,
        RouterLink,
        MatButtonModule,
        MatPaginatorModule,
        ProjectNrPipe,
        TimestampPipe
    ]
})
export class BudgetListTableComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() searchMode: 'quick' | 'slow';
    @Input() queryParamFilter: boolean;

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    dataSource = new MatTableDataSource<Budget>([]);
    displayedColumns = DEFAULT_VIEW_COLUMNS;

    totalElements = 0;

    @Output() updateTotalElements: EventEmitter<number> = new EventEmitter<number>();
    appliedFilter: BudgetListFilter;
    private unsubscribe$: Subject<void> = new Subject<void>();
    private firstLoad = true;
    private isLoadingData: Subscription;

    protected readonly OpportunityStages = OpportunityStages;
    protected readonly OpportunityStatuses = OpportunityStatuses;

    constructor(
        private budgetListTableService: BudgetListTableService,
        private authService: AuthService,
        private router: Router,
        private projectNrPipe: ProjectNrPipe
    ) {
    }

    get tableIsLoading$() {
        return this.budgetListTableService.isLoading$(this.searchMode);
    }

    ngOnInit(): void {
        this.paginator.pageSize = 10;

        this.budgetListTableService.appliedFilterAndSortingAndPagination$.pipe(
            filter(newFilter => this.budgetListTableService.compareFilters(newFilter, this.appliedFilter)),
            takeUntil(this.unsubscribe$)
        ).subscribe(inputFilter => this.handleFilterChange(inputFilter));

        this.authService.userHasPermission('app.budget.final.read')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((val) => {
                if (val) {
                    if (!this.displayedColumns.includes('balances')) {
                        this.displayedColumns = [...this.displayedColumns, 'balances'];
                    }
                } else {
                    const index = this.displayedColumns.indexOf('balances');
                    if (index !== -1) {
                        this.displayedColumns.splice(index, 1);
                    }
                }
            });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    ngAfterViewInit() {
        this.sort.sortChange
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.paginator.pageIndex = 0;
                this.budgetListTableService.storeSortingAndPageSize(this.sort.active, this.sort.direction, this.paginator.pageSize);
                if (this.searchMode === 'quick') {
                    this.updateBudgetListIds();
                }
            });

        this.paginator.page
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.budgetListTableService.storeSortingAndPageSize(this.sort.active, this.sort.direction, this.paginator.pageSize);
                if (this.searchMode === 'quick') {
                    this.updateBudgetListIds();
                }
            });

        if (this.searchMode === 'slow') {
            this.paginator.page.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.loadDataFromBackend());
        } else {
            this.dataSource.paginator = this.paginator;
            this.dataSource.filterPredicate = this.quickTableFilterPredicate();
            this.dataSource.sort = this.sort;
            this.dataSource.sortData = this.quickTableSort();
        }
    }

    public goToBudget(row: Budget) {
        this.updateBudgetListIds();
        const dest = '/budget/' + row.id;
        this.router.navigate([dest]);
    }

    loadDataFromBackend() {
        this.dataSource.data = [];
        if (this.isLoadingData) {
            this.isLoadingData.unsubscribe();
        }

        this.isLoadingData = this.budgetListTableService.loadData(this.searchMode, this.paginator.pageIndex, this.paginator.pageSize)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(result => {
                if (!result?.data) {
                    return;
                }
                for (const budget of result.data) {
                    if (budget['deliveryThr']) {
                        budget['deliveryThr'] = firestoreDateToJsDate(budget['deliveryThr']);
                    } else {
                        budget['deliveryThr'] = null;
                    }

                    budget.creation = firestoreDateToJsDate(budget.creation);
                }
                this.setDataSource(result);
            });
    }

    isDeliveryDateExpired(budget: Budget) {
        if (budget.deliveryThr && ['new', 'assigned'].includes(budget.status)) {
            const date = (budget.deliveryThr instanceof Date) ? budget.deliveryThr : firestoreDateToJsDate(budget.deliveryThr);
            const dateDiff = moment(date).diff(moment(new Date()), 'days');
            return dateDiff < 0 ? 'red' : (dateDiff < 7) ? 'orange' : 'black';
        }

        return 'black';
    }

    private handleFilterChange(inputFilter: BudgetListFilter) {
        this.restoreSorting(inputFilter);
        this.restorePageSize(inputFilter);
        this.appliedFilter = {...inputFilter};
        this.paginator.pageIndex = 0;

        if (this.searchMode === 'quick') {
            this.dataSource.filter = JSON.stringify(inputFilter);
            this.updateBudgetListIds();
        }

        if (this.searchMode === 'slow' || this.firstLoad) {
            this.loadDataFromBackend();
        }
    }

    private setDataSource(response: BudgetListResponse) {
        if (this.searchMode === 'slow') {
            this.dataSource = new MatTableDataSource<Budget>(response.data ?? []);
        } else {
            this.dataSource.data = response.data ?? [];
        }

        this.totalElements = response.totalSize;
        this.paginator.length = response.totalSize;

        if (this.firstLoad) {
            this.firstLoad = false;
            if (this.searchMode === 'quick') {
                setTimeout(() => {
                    this.dataSource.filter = JSON.stringify(this.appliedFilter);
                    this.updateBudgetListIds();
                });
            } else if (this.searchMode === 'slow') {
                this.updateBudgetListIds();
            }
        } else {
            this.updateBudgetListIds();
        }

        this.updateTotalElements.emit(this.totalElements);
    }

    private updateBudgetListIds() {
        if (this.searchMode === 'quick') {
            const filteredSorted = this.dataSource._orderData(this.dataSource.filteredData);
            const paged = this.dataSource._pageData ? this.dataSource._pageData(filteredSorted) : this.sliceCurrentPage(filteredSorted);
            const ids = paged.map(b => b.id).filter(id => !!id);
            this.budgetListTableService.setQuickBudgetListIds(ids);
        } else if (this.searchMode === 'slow') {
            const data = this.dataSource?.data ?? [];
            const ids = data.map(b => b.id).filter(id => !!id);
            this.budgetListTableService.setQuickBudgetListIds(ids);
        }
    }

    private sliceCurrentPage(data: Budget[]): Budget[] {
        if (!this.paginator || !data) {
            return data ?? [];
        }
        const startIndex = (this.paginator.pageIndex || 0) * (this.paginator.pageSize || 10);
        return data.slice(startIndex, startIndex + (this.paginator.pageSize || 10));
    }

    private restoreSorting(inputFilter: BudgetListFilter) {
        const needRefresh = this.sort.active !== inputFilter.sortActive || this.sort.direction !== inputFilter.sortDirection;
        this.sort.active = (inputFilter.sortActive) ? inputFilter.sortActive : 'creation';
        this.sort.direction = (inputFilter.sortDirection) ? inputFilter.sortDirection as SortDirection : 'desc';
        if (needRefresh) {
            this.sort.sortChange.emit();
        }
    }

    private restorePageSize(inputFilter: BudgetListFilter) {
        const needRefresh = this.paginator.pageSize !== inputFilter.pageSize;
        this.paginator.pageSize = (inputFilter.pageSize) ? inputFilter.pageSize : 10;
        if (needRefresh) {
            this.paginator.page.emit();
        }
    }

    private quickTableFilterPredicate(): (budget: Budget, filterValue: string) => boolean {
        return (budget: Budget, inputFilter: string) => {
            if (!inputFilter) {
                return true;
            }
            const filterValue = JSON.parse(inputFilter);

            const dateFrom = filterValue.dateFrom ? new Date(filterValue.dateFrom) : null;
            const dateTo = filterValue.dateTo ? new Date(filterValue.dateTo) : null;

            const budgetCreation = new Date(budget.creation);
            if (dateFrom && budgetCreation <= dateFrom) {
                return false;
            }
            if (dateTo && budgetCreation >= dateTo) {
                return false;
            }

            let searchStr = budget.budgetNr || '';
            if (budget.project) {
                searchStr += this.projectNrPipe.transform(budget);
            }
            if (budget.company?.companyName) {
                searchStr += budget.company.companyName;
            }
            if (budget.project?.projectManager) {
                searchStr += budget.project.projectManager.displayName;
            }
            if (budget.assignee) {
                searchStr += budget.assignee.displayName;
            }
            if (budget.budgetName) {
                searchStr += budget.budgetName;
            }
            if (budget.author) {
                searchStr += budget.author.displayName;
            } else {
                searchStr += budget.description;
            }

            searchStr = searchStr.toLowerCase();

            let iconSearchStr = [undefined, null, ''].includes(budget.status) ? 'default' : budget.status;
            iconSearchStr = iconSearchStr.toLowerCase();

            const filterCondition = (filterValue.filter) ? searchStr.indexOf(filterValue.filter.toLowerCase()) !== -1 : true;
            const statusCondition = (filterValue.status) ? iconSearchStr.includes(filterValue.status.toLowerCase()) : true;
            return (filterCondition && statusCondition) && (filterValue.showExport || (!budget.export));
        };
    }

    private quickTableSort(): (items: Budget[], sort: MatSort) => Budget[] {
        return (items: Budget[], sort: MatSort) => {
            const sortDirection = (sort.direction === 'asc') ? 1 : -1;
            switch (sort.active) {
                case 'status':
                    return items.sort((a, b) => a.status.localeCompare(b.status) * sortDirection);
                case 'budgetNr':
                    return items.sort((a, b) => a.budgetNr.localeCompare(b.budgetNr) * sortDirection);
                case 'manager':
                    return items.sort((a, b) => {
                        const managerA = a.project ? (a.project.projectManager ? a.project.projectManager?.displayName : '') : '';
                        const managerB = b.project ? (b.project.projectManager ? b.project.projectManager?.displayName : '') : '';
                        return managerA.localeCompare(managerB) * sortDirection;
                    });
                case 'projectNr':
                    return items.sort((a, b) => {
                        const prjNrA = a.project ? a.project.projectNr : '';
                        const prjNrB = b.project ? b.project.projectNr : '';
                        return prjNrA.localeCompare(prjNrB) * sortDirection;
                    });
                case 'pwName':
                    return items.sort((a, b) => {
                        const nameA = a.budgetName ? a.budgetName : a.description ? a.description : '';
                        const nameB = b.budgetName ? b.budgetName : b.description ? b.description : '';
                        return nameA.localeCompare(nameB) * sortDirection;
                    });
                case 'pwCompanyName':
                    return items.sort((a, b) => {
                        const companyA = a.company?.companyName || '';
                        const companyB = b.company?.companyName || '';
                        return companyA.localeCompare(companyB) * sortDirection;
                    });
                case 'author':
                    return items.sort((a, b) => {
                        const authA = a.author ? a.author.displayName : '';
                        const authB = b.author ? b.author.displayName : '';
                        return authA.localeCompare(authB) * sortDirection;
                    });
                case 'category':
                    return items.sort((a, b) => (a.category.localeCompare(b.category) * sortDirection));
                case 'creation':
                    return items.sort((a, b) => (a.creation.getTime() - b.creation.getTime()) * sortDirection);
                case 'deliveryThr':
                    return items.sort((a, b) => {
                        const dateA = (<Date>a.deliveryThr) ? (<Date>a.deliveryThr).getTime() : 0;
                        const dateB = (<Date>b.deliveryThr) ? (<Date>b.deliveryThr).getTime() : 0;
                        return (dateA - dateB) * sortDirection;
                    });
            }
        };
    }
}

const DEFAULT_VIEW_COLUMNS = [
    'status',
    'projectNr',
    'manager',
    'budgetNr',
    'pwName',
    'pwCompanyName',
    'author',
    'creation',
    'deliveryThr'
];
